import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X, Loader2, ImageIcon, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, adminCall, fmt, previewSrc, formatApiError, CATEGORY_LABELS } from "../../api";

const EMPTY = {
  name: "",
  description: "",
  category: "study",
  price: "",
  compare_at: "",
  formats: "",
  license_summary: "",
  previews: [],
  featured: false,
  active: true,
};

const input =
  "w-full rounded-xl border border-sage/20 bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-sage";

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | {form, id}
  const [busy, setBusy] = useState(false);
  const previewInput = useRef(null);
  const fileInput = useRef(null);

  const load = () =>
    adminCall(() => api.get("/admin/products"))
      .then((r) => setProducts(r.data.products))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setEditing({ id: null, form: { ...EMPTY } });
  const openEdit = (p) =>
    setEditing({
      id: p.id,
      files: p.files || [],
      form: {
        name: p.name,
        description: p.description,
        category: p.category,
        price: (p.price_cents / 100).toFixed(2),
        compare_at: p.compare_at_price_cents ? (p.compare_at_price_cents / 100).toFixed(2) : "",
        formats: (p.formats || []).join(", "),
        license_summary: p.license_summary || "",
        previews: p.previews || [],
        featured: !!p.featured,
        active: !!p.active,
      },
    });

  const save = async () => {
    const f = editing.form;
    const body = {
      name: f.name,
      description: f.description,
      category: f.category,
      price: parseFloat(f.price),
      compare_at_price: f.compare_at ? parseFloat(f.compare_at) : null,
      formats: f.formats.split(",").map((s) => s.trim()).filter(Boolean),
      license_summary: f.license_summary,
      previews: f.previews,
      featured: f.featured,
      active: f.active,
    };
    setBusy(true);
    try {
      if (editing.id) await adminCall(() => api.put(`/admin/products/${editing.id}`, body));
      else {
        const { data } = await adminCall(() => api.post("/admin/products", body));
        setEditing({ id: data.id, files: [], form: { ...f } });
      }
      toast.success("Product saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not save the product."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Existing orders keep their downloads.`)) return;
    await adminCall(() => api.delete(`/admin/products/${p.id}`)).catch((e) => toast.error(formatApiError(e)));
    toast.success("Product removed");
    load();
  };

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await adminCall(() => api.post(`/admin/upload?kind=${kind}`, fd));
    return data;
  };

  const onPreviewPick = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { path } = await upload(file, "preview");
      setEditing((s) => ({ ...s, form: { ...s.form, previews: [...s.form.previews, path] } }));
      toast.success("Preview uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed."));
    }
  };

  const onFilePick = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !editing?.id) {
      if (!editing?.id) toast.error("Save the product first, then attach its file.");
      return;
    }
    try {
      const { path, filename } = await upload(file, "file");
      const { data } = await adminCall(() =>
        api.post("/admin/products/attach-file", { product_id: editing.id, path, filename })
      );
      setEditing((s) => ({ ...s, files: data.files }));
      toast.success("File attached — attach several to sell a bundle");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed."));
    }
  };

  const detachFile = async (path) => {
    try {
      const { data } = await adminCall(() =>
        api.post("/admin/products/detach-file", { product_id: editing.id, path })
      );
      setEditing((s) => ({ ...s, files: data.files }));
      toast.success("File removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove the file."));
    }
  };

  return (
    <div data-testid="admin-products-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-ink">Products</h2>
        <button
          onClick={openNew}
          data-testid="admin-add-product-btn"
          className="flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-all hover:-translate-y-0.5 hover:bg-sage-deep"
        >
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-sage/12 bg-white/80" data-testid="admin-products-table">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-sage/10 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
              <th className="px-5 py-4">Product</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Price</th>
              <th className="px-5 py-4">File</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-sage/5 last:border-0" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-5 py-4 font-medium text-ink">{p.name}</td>
                <td className="px-5 py-4 text-ink/60">{CATEGORY_LABELS[p.category] || p.category}</td>
                <td className="px-5 py-4 font-mono text-xs text-sage-deep">{fmt(p.price_cents)}</td>
                <td className="px-5 py-4">
                  {p.has_file ? (
                    <span className="rounded-full bg-sage-tint px-3 py-1 text-[11px] text-sage-deep">Attached</span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] text-amber-700">No file</span>
                  )}
                </td>
                <td className="px-5 py-4 text-ink/60">{p.active ? "Live" : "Hidden"}</td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.slug}`} className="rounded-full border border-sage/20 p-2 text-sage transition-colors hover:bg-sage hover:text-cream" aria-label="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.slug}`} className="rounded-full border border-red-200 p-2 text-red-500 transition-colors hover:bg-red-500 hover:text-white" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-ink/45">No products yet — add your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 backdrop-blur-sm" data-testid="product-editor-modal">
          <div className="my-10 w-full max-w-2xl rounded-[1.6rem] bg-cream p-8 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-ink">{editing.id ? "Edit product" : "New product"}</h3>
              <button onClick={() => setEditing(null)} data-testid="editor-close-btn" className="rounded-full p-2 text-ink/50 hover:bg-cream-dark" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="mt-6 space-y-4">
              <input className={input} data-testid="editor-name" placeholder="Product name" value={editing.form.name}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })} />
              <div className="grid gap-4 sm:grid-cols-2">
                <select className={input} data-testid="editor-category" value={editing.form.category}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, category: e.target.value } })}>
                  {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <input className={input} data-testid="editor-price" type="number" min="0.5" step="0.01" placeholder="Price (USD)"
                  value={editing.form.price}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, price: e.target.value } })} />
              </div>
              <div>
                <input className={input} data-testid="editor-compare-at" type="number" min="0.5" step="0.01"
                  placeholder="Compare-at price (optional — shows as crossed-out &quot;was&quot; price)"
                  value={editing.form.compare_at}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, compare_at: e.target.value } })} />
              </div>
              <textarea className={input} data-testid="editor-description" rows={4} placeholder="Description"
                value={editing.form.description}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })} />
              <input className={input} data-testid="editor-formats" placeholder="Formats, comma separated (e.g. PDF (A4), GoodNotes)"
                value={editing.form.formats}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, formats: e.target.value } })} />
              <textarea className={input} data-testid="editor-license" rows={2} placeholder="Licence terms shown on the product page"
                value={editing.form.license_summary}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, license_summary: e.target.value } })} />
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-ink/70">
                  <input type="checkbox" data-testid="editor-featured" checked={editing.form.featured}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, featured: e.target.checked } })} />
                  Featured on homepage
                </label>
                <label className="flex items-center gap-2 text-sm text-ink/70">
                  <input type="checkbox" data-testid="editor-active" checked={editing.form.active}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })} />
                  Visible in shop
                </label>
              </div>

              <div className="rounded-2xl border border-sage/15 bg-white/70 p-5">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
                  <ImageIcon size={13} /> Preview images (public)
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {editing.form.previews.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={previewSrc(p)} alt="" className="h-20 w-16 rounded-lg border border-sage/15 object-cover" />
                      <button
                        onClick={() => setEditing({ ...editing, form: { ...editing.form, previews: editing.form.previews.filter((_, j) => j !== i) } })}
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-ink p-0.5 text-cream" aria-label="Remove preview">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => previewInput.current.click()} data-testid="editor-upload-preview"
                    className="flex h-20 w-16 items-center justify-center rounded-lg border border-dashed border-sage/30 text-sage transition-colors hover:bg-sage-tint" aria-label="Upload preview">
                    <Upload size={16} />
                  </button>
                  <input ref={previewInput} type="file" accept="image/*" className="hidden" onChange={onPreviewPick} />
                </div>
              </div>

              <div className="rounded-2xl border border-lavender/20 bg-lavender-tint/50 p-5">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-lavender-deep">
                  <FileDown size={13} /> Download files (private — never public). Attach several to sell a bundle.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button onClick={() => onFilePickButtonClick(fileInput, editing, toast)} data-testid="editor-upload-file"
                    className="rounded-full border border-lavender/40 px-4 py-2 text-xs font-medium text-lavender-deep transition-colors hover:bg-lavender hover:text-white">
                    Upload file
                  </button>
                  <input ref={fileInput} type="file" accept=".pdf,.zip,.png,.jpg,.jpeg" className="hidden" onChange={onFilePick} />
                  {!editing.id && (
                    <span className="text-xs text-ink/50">Save first, then attach files</span>
                  )}
                </div>
                {(editing.files || []).length > 0 && (
                  <div className="mt-3 space-y-2" data-testid="editor-files-list">
                    {editing.files.map((f) => (
                      <div key={f.path} className="flex items-center justify-between rounded-xl bg-white/80 px-4 py-2.5">
                        <span className="truncate text-xs text-ink/70">{f.name}</span>
                        <button onClick={() => detachFile(f.path)} data-testid={`detach-file-${f.name}`}
                          className="ml-3 rounded-full p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${f.name}`}>
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {editing.id && !(editing.files || []).length && (
                  <p className="mt-2 text-xs text-amber-600">No file attached — product can't be purchased yet</p>
                )}
              </div>

              <button onClick={save} disabled={busy} data-testid="editor-save-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-sage py-3.5 text-sm font-medium text-cream transition-colors hover:bg-sage-deep disabled:opacity-60">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Save product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function onFilePickButtonClick(fileInput, editing, toast) {
  if (!editing.id) {
    toast.error("Save the product first, then attach its file.");
    return;
  }
  fileInput.current.click();
}
